import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

export const updateBranding = createAsyncThunk('branding/update', async ({ ownerId, ownerType, branding }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/client-apps/${ownerId}/branding`, branding);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchThemeConfig = createAsyncThunk('branding/fetchTheme', async ({ tenantId }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/client-apps/tenant/${tenantId}`);
    const apps = response.data.data.apps || [];
    if (apps.length > 0) {
      return apps[0].branding;
    }
    return null;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

const brandingSlice = createSlice({
  name: 'branding',
  initialState: {
    branding: null,
    subdomain: null,
    customDomain: null,
    darkMode: false,
    theme: {
      primaryColor: '#4a90d9',
      accentColor: '#22c55e',
      backgroundColor: '#ffffff',
      darkModePrimaryColor: '#3b82f6',
      darkModeAccentColor: '#16a34a',
      darkModeBackgroundColor: '#1a1a2e',
      darkModeTextColor: '#e4e4e7',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      borderRadius: '8px',
      logo: null,
      favicon: null,
      title: 'MultiPaaS'
    },
    isLoading: false,
    error: null
  },
  reducers: {
    setBranding: (state, action) => {
      state.branding = action.payload;
      if (action.payload) {
        state.theme = {
          primaryColor: action.payload.primaryColor || '#4a90d9',
          accentColor: action.payload.accentColor || '#22c55e',
          backgroundColor: action.payload.backgroundColor || '#ffffff',
          darkModePrimaryColor: action.payload.darkModePrimaryColor || '#3b82f6',
          darkModeAccentColor: action.payload.darkModeAccentColor || '#16a34a',
          darkModeBackgroundColor: action.payload.darkModeBackgroundColor || '#1a1a2e',
          darkModeTextColor: action.payload.darkModeTextColor || '#e4e4e7',
          fontFamily: action.payload.fontFamily || '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          borderRadius: action.payload.borderRadius || '8px',
          logo: action.payload.logo || null,
          favicon: action.payload.favicon || null,
          title: action.payload.displayName || 'MultiPaaS'
        };
      }
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        if (state.darkMode) {
          root.classList.add('dark');
          root.style.setProperty('--bg-color', state.theme.darkModeBackgroundColor || '#1a1a2e');
          root.style.setProperty('--text-color', state.theme.darkModeTextColor || '#e4e4e7');
          root.style.setProperty('--primary-color', state.theme.darkModePrimaryColor || '#3b82f6');
          root.style.setProperty('--accent-color', state.theme.darkModeAccentColor || '#16a34a');
        } else {
          root.classList.remove('dark');
          root.style.setProperty('--bg-color', state.theme.backgroundColor || '#ffffff');
          root.style.setProperty('--text-color', '#333333');
          root.style.setProperty('--primary-color', state.theme.primaryColor);
          root.style.setProperty('--accent-color', state.theme.accentColor);
        }
      }
    },
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        if (action.payload) {
          root.classList.add('dark');
          root.style.setProperty('--bg-color', state.theme.darkModeBackgroundColor || '#1a1a2e');
          root.style.setProperty('--text-color', state.theme.darkModeTextColor || '#e4e4e7');
          root.style.setProperty('--primary-color', state.theme.darkModePrimaryColor || '#3b82f6');
          root.style.setProperty('--accent-color', state.theme.darkModeAccentColor || '#16a34a');
        } else {
          root.classList.remove('dark');
          root.style.setProperty('--bg-color', state.theme.backgroundColor || '#ffffff');
          root.style.setProperty('--text-color', '#333333');
          root.style.setProperty('--primary-color', state.theme.primaryColor);
          root.style.setProperty('--accent-color', state.theme.accentColor);
        }
      }
    },
    applyTheme: (state) => {
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', state.theme.primaryColor);
        root.style.setProperty('--accent-color', state.theme.accentColor);
        root.style.setProperty('--bg-color', state.theme.backgroundColor);
        root.style.setProperty('--font-family', state.theme.fontFamily);
        root.style.setProperty('--border-radius', state.theme.borderRadius);
        if (state.theme.logo) {
          root.style.setProperty('--logo-url', `url(${state.theme.logo})`);
        }
        document.title = state.theme.title;
      }
    },
    clearBranding: (state) => {
      state.branding = null;
      state.subdomain = null;
      state.customDomain = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemeConfig.fulfilled, (state, action) => {
        if (action.payload) {
          state.branding = action.payload;
          state.theme = {
            primaryColor: action.payload.primaryColor || '#4a90d9',
            accentColor: action.payload.accentColor || '#22c55e',
            backgroundColor: action.payload.backgroundColor || '#ffffff',
            darkModePrimaryColor: action.payload.darkModePrimaryColor || '#3b82f6',
            darkModeAccentColor: action.payload.darkModeAccentColor || '#16a34a',
            fontFamily: action.payload.fontFamily || '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
            borderRadius: action.payload.borderRadius || '8px',
            logo: action.payload.logo || null,
            favicon: action.payload.favicon || null,
            title: action.payload.displayName || 'MultiPaaS'
          };
        }
      });
  }
});

export const { setBranding, toggleDarkMode, setDarkMode, applyTheme, clearBranding } = brandingSlice.actions;
export default brandingSlice.reducer;